import gql from 'graphql-tag';

/**
 * Queries against the signiant API — see
 * content-acq-signiant/cdk/content_signiant/api.graphql.
 *
 * A portal's `status`, `files` and `processes` are lazily
 * resolved: each one costs a live call to the Media Shuttle portal, an S3
 * listing or a DynamoDB read, so only ask for them where they are actually
 * shown. The list page deliberately selects none of them.
 *
 * `browse` is the portal's own file tree and serves every capability — an RX
 * download portal to pick files from, an upload portal to confirm a delivery
 * arrived. It is
 * fetched one directory at a time, by the shared browser component, and
 * deliberately NOT part of the detail query: the whole-tree `files` field cannot
 * list a portal the size of thales-medianet inside the resolver's 30 s budget,
 * and having it in the detail query took the entire page down with it.
 *
 * A portal can have both capabilities (West Entertainment does), so these blocks
 * are not alternatives: both may come back populated.
 */

export const GET_PORTALS = gql`
  query GetPortals {
    portals {
      id
      name
      portalUrl
      lastSweepAt
      status {
        loggedIn
        username
        needsMfa
        message
      }
      download { portalFolder dropDestination pollIntervalMinutes autoDownloadWatermark }
      upload { sourceBucket sourcePrefix pollIntervalMinutes }
    }
  }
`;

/**
 * Start an import. Returns the process immediately in IN_PROGRESS — the actual
 * download/copy runs in a state machine, so the caller polls the portal's
 * `processes` (or `process(id)`) to follow it.
 */
export const IMPORT_FILE = gql`
  mutation ImportFile($portalId: ID!, $folderId: String!, $filePath: String!) {
    import(input: { portalId: $portalId, folderId: $folderId, filePath: $filePath }) {
      id
      state
      message
      action
      start_date
      data
    }
  }
`;

/**
 * Start a login. The portal answers MFA_REQUIRED for an account with MFA on
 * (all of ours), having mailed a code. The pre-MFA cookies stay server-side and
 * are the login-in-progress, so the follow-up carries only the code — nothing
 * sensitive reaches the browser.
 *
 * username/password are only for a portal that has none stored yet (status
 * NO_CREDENTIALS); the API keeps them in the portal's secret from then on.
 */
export const LOGIN = gql`
  mutation Login($portalId: ID!, $username: String, $password: String) {
    login(input: { portalId: $portalId, username: $username, password: $password }) {
      status
      message
    }
  }
`;

export const SUBMIT_MFA = gql`
  mutation SubmitMfa($portalId: ID!, $code: String!) {
    submit_mfa(input: { portalId: $portalId, code: $code }) {
      status
      message
    }
  }
`;

export const GET_PORTAL_DETAIL = gql`
  query GetPortalDetail($portalId: ID!) {
    portal(input: { portalId: $portalId }) {
      id
      name
      portalUrl
      lastSweepAt
      status {
        loggedIn
        username
        expiresAt
        needsMfa
        message
      }
      download {
        portalFolder
        dropDestination
        pollIntervalMinutes
        autoDownloadWatermark
      }
      upload {
        sourceBucket
        sourcePrefix
        pollIntervalMinutes
      }
      processes(input: { limit: 30 }) {
        id
        state
        message
        action
        start_date
        end_date
        data
      }
    }
  }
`;

/**
 * Just the process rows of a portal — no `files`, so it costs a DynamoDB read
 * rather than a live portal listing. Used to notice imports that were started
 * elsewhere (the scheduled sweep, another operator) while a page sits open.
 */
/**
 * One directory of a portal. Cheap and bounded — one portal REST call — which is
 * why browsing is level-at-a-time rather than a whole-tree listing. Pass the
 * `folderId` of the entry being descended into: a portal may expose several
 * member folders, so the path alone does not identify a directory.
 */
export const BROWSE_PORTAL = gql`
  query BrowsePortal($portalId: ID!, $path: String, $folderId: String) {
    portal(input: { portalId: $portalId }) {
      id
      browse(input: { path: $path, folderId: $folderId }) {
        name
        path
        isDirectory
        size
        modifiedAt
        folderId
      }
    }
  }
`;

export const GET_PORTAL_PROCESSES = gql`
  query GetPortalProcesses($portalId: ID!) {
    portal(input: { portalId: $portalId }) {
      id
      processes(input: { limit: 30 }) {
        id
        state
        message
        action
        start_date
        end_date
        data
      }
    }
  }
`;
